import { Chip, Paper, Typography } from '@material-ui/core'
import clsx from 'clsx'

import { getTranslate } from '../../localization'
import {
  PMSChannelCredential,
  PMSProviderCredentialHealth
} from '../../types/inventoryHealth'
import useStyles from './styles/CredentialPanel.styles'

type Props = {
  providers: PMSProviderCredentialHealth[]
}

/**
 * Adapters that authenticate with a PER-CHANNEL password.
 *
 * Verified against pmsmanager (2026-09-10) by reading every provider on both the
 * ARI and Booking paths. Only these two read the channel's own stored password:
 *
 *   Medric — Basic auth built from the channel's PMS code + PMS pass
 *   Rata   — bearer token minted from the channel's PMS pass
 *
 * Every other adapter (Haris, Medrik, Parak, SnappTrip, Asa, Mosafer, Parsian,
 * Protel, Raein) authenticates from pmsmanager's ENVIRONMENT — Haris, for one,
 * sends a single HARIS_USER / HARIS_PASS pair shared by the whole estate. For
 * those adapters an empty per-channel password is the NORMAL configuration and
 * says nothing at all about whether the channel can authenticate.
 *
 * Why this gate exists: the check below used to run for every adapter, so on
 * property 10700 all nine Haris channels were reported "Active, but no password
 * is stored ... Every call it makes fails, so its prices can never update" —
 * while four of them were demonstrably returning HTTP 200 with live rates. It
 * was the loudest claim on the screen and it was false on every row, and this
 * screen is written to be pasted into support tickets.
 *
 * Maintenance note: update this when an adapter changes how it authenticates.
 * A stale entry costs a false positive; a missing one costs a missed real
 * fault. Erring toward the SMALLER list is deliberate — a missed credential
 * fault still surfaces through the inventory rows, whereas a false "every call
 * fails" sends support chasing a credential that was never meant to exist.
 */
const PER_CHANNEL_CREDENTIAL_ADAPTERS = new Set(['Medric', 'Rata'])

export function usesPerChannelCredential(adapter: string | null): boolean {
  return adapter != null && PER_CHANNEL_CREDENTIAL_ADAPTERS.has(adapter)
}

/**
 * Severity of a channel's credential configuration.
 *
 * An active channel with no password can never authenticate, so its prices can
 * never update — that is a Critical regardless of what the inventory rows show,
 * because the rows only report the SYMPTOM of a credential that cannot log in.
 *
 * That reasoning holds ONLY where the adapter actually uses a per-channel
 * password, hence the `perChannelCredential` gate; see the list above.
 *
 * An inactive channel with no password is expected, not a fault.
 */
function credentialIssues(
  channel: PMSChannelCredential,
  perChannelCredential: boolean
): {
  severity: 'critical' | 'warning' | 'none'
  messages: string[]
} {
  const messages: string[] = []
  let severity: 'critical' | 'warning' | 'none' = 'none'

  if (perChannelCredential && channel.active && !channel.hasPMSPass) {
    messages.push(
      getTranslate(
        'Active, but no password is stored for this channel or its property. Every call it makes fails, so its prices can never update.'
      )
    )
    severity = 'critical'
  }

  if (channel.active && !channel.hasPMSCode) {
    messages.push(getTranslate('Active, but no PMS code is stored.'))
    severity = 'critical'
  }

  if (channel.sharesPassWith.length > 0) {
    messages.push(
      getTranslate('Shares its password with: {others}', {
        others: channel.sharesPassWith.join(', ')
      })
    )
    if (severity === 'none') {
      severity = 'warning'
    }
  }

  if (channel.pmsPassPadded) {
    messages.push(
      getTranslate(
        'The stored password has leading or trailing whitespace. It will display correctly everywhere and authenticate nowhere.'
      )
    )
    severity = 'critical'
  }

  if (channel.pmsCodePadded) {
    messages.push(
      getTranslate('The stored PMS code has leading or trailing whitespace.')
    )
    severity = 'critical'
  }

  return { severity, messages }
}

export default function CredentialPanel({ providers }: Props) {
  const classes = useStyles()

  if (providers.length === 0) {
    return (
      <Paper className={classes.panel}>
        <Typography variant="body2" color="textSecondary">
          {getTranslate(
            'Credential health is unavailable. The inventory comparison below is unaffected.'
          )}
        </Typography>
      </Paper>
    )
  }

  const headings = ['Channel', 'Active', 'PMS code', 'Password', 'Notes']

  return (
    <>
      {providers.map((provider) => (
        <Paper
          key={`${provider.providerCode}-${provider.providerAdapter}`}
          className={classes.panel}>
          <Typography variant="subtitle1">
            {getTranslate('Channel credentials')} — {provider.providerName} (
            {provider.providerAdapter})
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {provider.providerURL}
          </Typography>

          <div role="table" aria-label={getTranslate('Channel credentials')}>
            <div className={classes.tableHead} role="row">
              {headings.map((heading) => (
                <div key={heading} role="columnheader">
                  <Typography variant="body1">
                    {getTranslate(heading)}
                  </Typography>
                </div>
              ))}
            </div>

            {provider.channels.map((channel) => {
              const { severity, messages } = credentialIssues(
                channel,
                usesPerChannelCredential(provider.providerAdapter)
              )
              const tone =
                severity === 'critical'
                  ? classes.rowCritical
                  : severity === 'warning'
                  ? classes.rowWarning
                  : classes.rowNormal

              return (
                <div
                  key={channel.channelID || channel.channelCode}
                  className={clsx(classes.row, tone)}
                  role="row">
                  <div role="cell">
                    <Typography variant="body2">
                      {channel.channelName || channel.channelCode}
                    </Typography>
                  </div>
                  <div role="cell">
                    <Typography variant="body2">
                      {channel.active
                        ? getTranslate('Yes')
                        : getTranslate('No')}
                    </Typography>
                  </div>
                  <div role="cell">
                    {channel.hasPMSCode ? (
                      <Typography variant="body2">
                        {getTranslate('Stored')}
                      </Typography>
                    ) : (
                      <Chip size="small" label={getTranslate('Missing')} />
                    )}
                  </div>
                  <div role="cell">
                    {channel.hasPMSPass ? (
                      /* Length only. The value is never sent to the panel. */
                      <Typography variant="body2">
                        {getTranslate('Stored ({length} characters)', {
                          length: channel.pmsPassLength
                        })}
                      </Typography>
                    ) : (
                      <Chip size="small" label={getTranslate('Missing')} />
                    )}
                  </div>
                  <div role="cell">
                    {messages.length === 0
                      ? '—'
                      : messages.map((m) => (
                          <Typography key={m} variant="body2">
                            {m}
                          </Typography>
                        ))}
                  </div>
                </div>
              )
            })}
          </div>
        </Paper>
      ))}
    </>
  )
}
